import { NavLink } from 'react-router-dom';
import { Plus, MoreHorizontal, ChevronDown, ChevronRight, UserPlus, Folder, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { getWorkspaceIcon, getProjectIcon } from './constants';

const WorkspaceItem = ({
  workspace,
  isNested,
  isExpanded,
  isCurrentWorkspace,
  onToggle,
  projects,
  loadingProjects,
  activeProjectId,
  onSwitchWorkspace,
  onCreateProject,
  onMoveToFolder,
  onInvite,
  onOpenSettings,
  onOpenProjectSettings,
  workspaceUnread = {},
  projectUnread = {}
}) => {
  const ws = workspace;
  const wsUnreadCount = workspaceUnread[ws.workspace_id] || 0;
  
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={`rounded-lg ${isCurrentWorkspace ? 'bg-[#4A9CC7]/5' : ''} ${wsUnreadCount > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
        <CollapsibleTrigger asChild>
          <div
            className={`flex items-center gap-2 py-1.5 rounded-lg cursor-pointer hover:bg-muted group ${isNested ? 'px-3' : 'px-2'}`}
            data-testid={`workspace-${ws.workspace_id}`}
          >
            <div className="flex items-center justify-center w-5 h-5">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
              ws.workspace_type === 'team' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' :
              ws.workspace_type === 'client' ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' :
              'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
            }`}>
              {getWorkspaceIcon(ws.workspace_type)}
            </div>
            <span 
              className={`flex-1 truncate text-xs font-medium text-foreground min-w-[60px] ${wsUnreadCount > 0 ? 'font-semibold' : ''}`}
              title={ws.name}
              onClick={(e) => {
                e.stopPropagation();
                onSwitchWorkspace(ws);
              }}
            >
              {ws.name}
            </span>
            
            {/* Unread badge */}
            {wsUnreadCount > 0 && (
              <Badge 
                className="bg-[#4A9CC7] text-white text-[10px] px-1.5 py-0 min-w-[18px] h-4 flex items-center justify-center"
                data-testid={`workspace-unread-${ws.workspace_id}`}
              >
                {wsUnreadCount > 99 ? '99+' : wsUnreadCount}
              </Badge>
            )}
            
            {/* Quick actions on hover */}
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                onClick={(e) => onCreateProject(ws, e)}
                title="Add Project"
              >
                <Plus className="w-3 h-3" />
              </Button>
              {(ws.user_role === 'owner' || ws.user_role === 'admin') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                  onClick={(e) => onMoveToFolder(ws, e)}
                  title="Move to Folder"
                >
                  <Folder className="w-3 h-3" />
                </Button>
              )}
              {ws.workspace_type !== 'personal' && (ws.user_role === 'owner' || ws.user_role === 'admin') && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                    onClick={(e) => onInvite(ws, e)}
                    title="Invite"
                  >
                    <UserPlus className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                    onClick={(e) => onOpenSettings(ws, e)}
                    title="Settings"
                  >
                    <MoreHorizontal className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className={`border-l border-border space-y-0.5 py-1 ${isNested ? 'ml-8 pl-2' : 'ml-6 pl-2'}`}>
            {loadingProjects ? (
              <div className="text-xs text-muted-foreground py-1 px-2">Loading...</div>
            ) : (projects || []).length === 0 ? (
              <button
                onClick={() => onCreateProject(ws)}
                className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground hover:text-[#4A9CC7] w-full"
              >
                <Plus className="w-3 h-3" />
                Add first project
              </button>
            ) : (
              <>
                {(projects || []).map(proj => {
                  const isActive = activeProjectId === proj.project_id;
                  const projUnreadCount = projectUnread[proj.project_id] || 0;
                  return (
                    <div
                      key={proj.project_id}
                      className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-all group/proj ${
                        isActive ? 'font-medium shadow-sm' : 'text-muted-foreground hover:bg-muted'
                      } ${projUnreadCount > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      style={isActive ? {
                        backgroundColor: `${proj.color || '#4A9CC7'}20`,
                        color: proj.color || '#4A9CC7',
                        borderLeft: `3px solid ${proj.color || '#4A9CC7'}`
                      } : {}}
                      data-testid={`project-${proj.project_id}`}
                    >
                      <NavLink
                        to={`/tasks?workspace=${ws.workspace_id}&project=${proj.project_id}`}
                        className="flex items-center gap-2 flex-1 min-w-0"
                        title={proj.name}
                      >
                        <div 
                          className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            backgroundColor: `${proj.color || '#4A9CC7'}20`,
                            color: proj.color || '#4A9CC7'
                          }}
                        >
                          {getProjectIcon(proj.icon)}
                        </div>
                        <span className={`truncate ${projUnreadCount > 0 ? 'font-semibold text-foreground' : ''}`}>{proj.name}</span>
                      </NavLink>
                      
                      {/* Project unread badge */}
                      {projUnreadCount > 0 && (
                        <Badge 
                          className="bg-[#4A9CC7] text-white text-[10px] px-1 py-0 min-w-[16px] h-4 flex items-center justify-center"
                          data-testid={`project-unread-${proj.project_id}`}
                        >
                          {projUnreadCount > 99 ? '99+' : projUnreadCount}
                        </Badge>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 opacity-0 group-hover/proj:opacity-100 text-muted-foreground hover:text-[#4A9CC7] flex-shrink-0"
                        onClick={(e) => onOpenProjectSettings(proj, ws, e)}
                        title="Project Settings"
                      >
                        <MoreHorizontal className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
                <button
                  onClick={() => onCreateProject(ws)}
                  className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground hover:text-[#4A9CC7] w-full"
                >
                  <Plus className="w-3 h-3" />
                  Add project
                </button>
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default WorkspaceItem;
